import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { isAfter } from 'date-fns'
import { flatten, find } from 'lodash'

import Api from 'services/Api'
import { Button } from 'components/Forms'
import BaseModal from 'components/Modals/BaseModal'
import { EmployeeSelectionToolCriteriaForm, EmployeeSelectionToolUserTable, EventSummaryList } from 'components/Schedule'
import { Loading, Collapsable } from 'components/UI'
import { Alert, Button as MerinioButton, Card } from 'ui'
import { closeModal } from 'store/actions/modals'
import { exportUsersAsCSV } from 'Manager/Users/userFunctions'
import { parseToTemplate } from 'services/ScheduleTemplates'
import { updateEvents } from 'store/actions/scheduleTemplates'
import { Parse } from 'services/Dates'
import { getPositionName, getUserName } from 'services/Utilities'
import { t } from 'translations'

const ScheduleTemplateESTModal = ({ scheduleTemplateId, ids = [], inTemplate }) => {
  const { selectedResourcesMap: selectedUsers = {} } = useSelector(state => state.tables['st-employee-selection-tool'] || {})
  const { events: templateEvents = [] } = useSelector(state => state?.scheduleTemplates)
  const { usersById } = useSelector(state => state.resources)
  const dispatch = useDispatch()

  const [template, setTemplate] = useState()
  const [appliedDates, setAppliedDates] = useState([])
  const [errorLoading, setErrorLoading] = useState(false)
  const [idsToAssign, setIdsToAssign] = useState(ids)

  const events = (inTemplate ? templateEvents : template?.events || []).filter(({ id, eventId }) => idsToAssign.includes(id || eventId))

  const usersToAssign = Object.keys(selectedUsers || {})
  const isDisabled = !usersToAssign.length
  const [isLoading, setIsLoading] = useState()

  // Calculates which user will be assigned to which shift depending on current selection
  const calculateEventAssignment = events => {
    let tooManyUsersSelected = false
    let someShiftsAreAssigned = false

    // Array of arrays of events, indexed by multiDayIndex
    const eventsPerDay = events.map(parseToTemplate).reduce((acc, event) => {
      if (event.employeeId) someShiftsAreAssigned = true
      if (!Array.isArray(acc[event.multiDayIndex])) acc[event.multiDayIndex] = []
      acc[event.multiDayIndex].push(event)
      return acc
    }, [])

    // The first user selected gets assigned to the first shift of each day, second user to second shift etc.
    for (const day of eventsPerDay) {
      if (day.length < usersToAssign.length) tooManyUsersSelected = true
      for (let index = 0; index < day.length; index++) {
        if (!usersToAssign[index]) break
        const event = day[index]
        event.employeeId = usersToAssign[index]
      }
    }

    // Warn user if some shifts are already assigned as well as if too many users are selected (more than number of shifts in any day)
    return { assignedEvents: flatten(eventsPerDay), tooManyUsersSelected, someShiftsAreAssigned }
  }

  const { assignedEvents, tooManyUsersSelected, someShiftsAreAssigned } = calculateEventAssignment(events)

  const fetchScheduleTemplate = useCallback(async () => {
    const { result, success } = await Api(`schedule/template`, { params: { _id: scheduleTemplateId, useNewReturn: true } })
    if (!success || !result.length) return setErrorLoading(true)
    setAppliedDates(Object.keys(result[0].dates).filter(date => isAfter(Parse(date, { inFormat: 'date' }), new Date())))
    return setTemplate(result[0])
  }, [scheduleTemplateId])

  useEffect(() => {
    fetchScheduleTemplate()
  }, [fetchScheduleTemplate])

  const assignUsers = async events => {
    setIsLoading(true)
    if (inTemplate) {
      // Update locally if in template
      dispatch(updateEvents(events))
      dispatch(closeModal())
    } else {
      // Update remotely otherwise
      template.events = template.events.map(event => {
        let assignedEvent = find(assignedEvents, { eventId: event.eventId })
        if (assignedEvent) event.employeeId = assignedEvent.employeeId
        return event
      })
      await Api.put(`schedule/template`, template)
      fetchScheduleTemplate()
      setIdsToAssign([])
    }
    setIsLoading()
  }

  if (errorLoading)
    return <BaseModal title={`manager.scheduleTemplates.employeeSelectionTool.MODAL`}>{t('manager.scheduleTemplates.TEMPLATE_DELETED')}</BaseModal>
  if (!template) return <Loading />

  return (
    <BaseModal hideButtons title={`manager.scheduleTemplates.employeeSelectionTool.MODAL`} wideModal>
      {!inTemplate ? <SelectEventsToAssign events={template?.events} setIdsToAssign={setIdsToAssign} /> : null}
      {/* Show events that will end up in the list */}
      <div className="mt-2">
        <EventSummaryList events={assignedEvents} inScheduleTemplate isTime />
      </div>
      <Alert variant="warning" className="my-2" show={!!tooManyUsersSelected || !!someShiftsAreAssigned}>
        {tooManyUsersSelected ? <div>{t('manager.scheduleTemplates.employeeSelectionTool.TOO_MANY_USERS_SELECTED')}</div> : null}
        {someShiftsAreAssigned ? <div>{t('manager.scheduleTemplates.employeeSelectionTool.SOME_SHIFTS_ASSIGNED')}</div> : null}
      </Alert>
      <EmployeeSelectionToolCriteriaForm events={events} template={template} appliedDates={appliedDates} />
      <Card maxHeight="40rem" className="mt-2">
        <EmployeeSelectionToolUserTable />
      </Card>
      <div className="mt-2">
        <Button
          className="btn btn-primary mr-2"
          text="manager.scheduleTemplates.employeeSelectionTool.ASSIGN_EMPLOYEES"
          onClick={() => assignUsers(assignedEvents)}
        />
        <Button
          className="btn btn-outline-secondary mr-2"
          disabled={isDisabled}
          isSubmitting={isLoading}
          onClick={() => exportUsersAsCSV(usersToAssign.map(_id => usersById[_id]))}
        >
          {t('manager.schedule.QUICK_ASSIGN.EXPORT_SELECTION')}
        </Button>
        <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} />
      </div>
    </BaseModal>
  )
}

const SelectEventsToAssign = ({ events, setIdsToAssign }) => {
  const [selectedGroup, setSelectedGroup] = useState()
  let shiftGroups = {}
  let assignedShifts = []

  // Split into assigned shifts and groups of unassigned shifts
  for (const event of events) {
    if (event.employeeId) {
      assignedShifts.push({
        key: event.eventId,
        name: `${getUserName(event.employeeId)} - ${getPositionName(event.positionId)} (${event.start} - ${event.end})`
      })
    } else {
      let key = `${event.start}:${event.end}:${event.positionId}`
      if (!shiftGroups[key]) {
        shiftGroups[key] = {
          key,
          name: `${getPositionName(event.positionId)} (${event.start} - ${event.end})`,
          ids: [event.eventId]
        }
      } else {
        shiftGroups[key].ids.push(event.eventId)
      }
    }
  }

  return (
    <>
      <Collapsable type="unassignedShifts" title="manager.scheduleTemplates.employeeSelectionTool.UNASSIGNED_SHIFTS" show>
        <div className="row">
          {Object.values(shiftGroups).map(({ key, name, ids }) => (
            <div className="col-4 mb-2" key={key}>
              <MerinioButton
                color="secondary"
                size="small"
                className="btn-block"
                onClick={() => {
                  setSelectedGroup(key)
                  setIdsToAssign(ids)
                }}
                outlined
              >
                <i className={selectedGroup === key ? 'fas fa-check-circle mr-1' : 'far fa-circle mr-1'} />
                <b>{ids.length}x</b> {name}
              </MerinioButton>
            </div>
          ))}
        </div>
      </Collapsable>
      <Collapsable type="assignedShifts" title="manager.scheduleTemplates.employeeSelectionTool.ASSIGNED_SHIFTS">
        <div className="row">
          {assignedShifts.map(({ key, name }) => (
            <div className="col-4 mb-2" key={key}>
              <MerinioButton
                color="secondary"
                size="small"
                className="btn-block"
                onClick={() => {
                  setSelectedGroup(key)
                  setIdsToAssign([key])
                }}
                outlined
              >
                <i className={selectedGroup === key ? 'fas fa-check-circle mr-1' : 'far fa-circle mr-1'} />
                {name}
              </MerinioButton>
            </div>
          ))}
        </div>
      </Collapsable>
    </>
  )
}

export default ScheduleTemplateESTModal
